import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import {
  Users, AlertCircle, Stethoscope, TrendingUp, HeartPulse, ArrowUpRight,
  Clock, Plus, CheckCircle2, Activity, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { RISK } from "@/components/PatientPicker";
import RecallModal from "@/components/RecallModal";
import CheckinAlerts from "@/components/CheckinAlerts";

function fmt(iso) { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return iso; } }

export default function Overview() {
  const [ov, setOv] = useState(null);
  const [recalls, setRecalls] = useState([]);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallEdit, setRecallEdit] = useState(null);
  const navigate = useNavigate();

  const loadOverview = useCallback(() => api.get("/dashboard/overview").then((r) => setOv(r.data)).catch(() => {}), []);
  const loadRecalls = useCallback(() => api.get("/recalls/board").then((r) => setRecalls(r.data.recalls || [])).catch(() => {}), []);
  useEffect(() => { loadOverview(); loadRecalls(); }, [loadOverview, loadRecalls]);

  const openNewRecall = () => { setRecallEdit(null); setRecallOpen(true); };
  const openEditRecall = (r) => { setRecallEdit(r); setRecallOpen(true); };
  const onRecallSaved = () => { loadRecalls(); loadOverview(); };

  const k = ov?.kpis || {};
  const cards = [
    { label: "Synced Patients", value: k.synced_patients ?? "—", icon: Users, tint: "teal", tid: "kpi-synced-patients" },
    { label: "Overdue Recalls", value: k.overdue_recalls ?? "—", icon: AlertCircle, tint: "amber", tid: "kpi-overdue-recalls" },
    { label: "Active Plans", value: k.active_plans ?? "—", icon: Stethoscope, tint: "blue", tid: "kpi-active-plans" },
    { label: "To Contact", value: k.patients_to_contact ?? "—", icon: HeartPulse, tint: "rose", tid: "kpi-to-contact" },
  ];
  const tints = {
    teal: "bg-teal-50 text-teal-600", amber: "bg-amber-50 text-amber-600",
    blue: "bg-blue-50 text-blue-600", rose: "bg-rose-50 text-rose-600",
  };

  return (
    <Layout title="Overview" subtitle="Clinic command centre — retention, recalls & plan activity"
      actions={<Button onClick={() => navigate("/builder")} data-testid="quick-build-plan-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="mr-1.5 h-4 w-4" /> Build Plan</Button>}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={c.tid}>
            <div className="flex items-start justify-between">
              <div className={`grid h-10 w-10 place-items-center rounded-xl ${tints[c.tint]}`}><c.icon className="h-5 w-5" /></div>
              <ArrowUpRight className="h-4 w-4 text-slate-300" />
            </div>
            <div className="mt-3 font-heading text-3xl font-extrabold tabular-nums text-slate-900">{c.value}</div>
            <div className="text-sm text-slate-500">{c.label}</div>
          </motion.div>
        ))}
      </div>

      <CheckinAlerts />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Retention trend + list */}
        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-teal-600" /><span className="font-heading text-sm font-bold text-slate-800">Patients on-cadence</span></div>
              <span className="font-heading text-2xl font-extrabold tabular-nums text-teal-700">{k.retention_rate ?? "—"}%</span>
            </div>
            <div className="mt-4 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={ov?.trend || []} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0d9488" stopOpacity={0.35} /><stop offset="100%" stopColor="#0d9488" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="retained" stroke="#0d9488" strokeWidth={2.5} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-heading text-sm font-bold text-slate-800">Who to contact</span>
              <button onClick={() => navigate("/retention")} className="text-xs font-medium text-teal-700 hover:underline" data-testid="view-retention-link">View all →</button>
            </div>
            <div className="space-y-2">
              {(ov?.top_flags || []).map((f) => (
                <div key={f.cliniko_id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5" data-testid={`overview-flag-${f.cliniko_id}`}>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${RISK[f.risk]}`}>{f.risk}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800">{f.name}</div>
                    <div className="truncate text-xs text-slate-500">{f.reason}</div>
                  </div>
                  <span className="font-mono text-xs text-slate-400 tabular-nums">{f.days_overdue}d</span>
                </div>
              ))}
              {(ov?.top_flags || []).length === 0 && <div className="py-4 text-center text-sm text-slate-400">No patients flagged.</div>}
            </div>
          </div>
        </div>

        {/* Recalls */}
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" /><span className="font-heading text-sm font-bold text-slate-800">Cliniko recalls</span></div>
              <button onClick={openNewRecall} data-testid="new-recall-btn" className="inline-flex items-center gap-1 rounded-lg bg-teal-50 px-2.5 py-1.5 text-[11px] font-semibold text-teal-700 transition-colors hover:bg-teal-100"><Plus className="h-3.5 w-3.5" /> New recall</button>
            </div>
            <div className="space-y-2" data-testid="recalls-list">
              {recalls.map((r) => (
                <div key={r.id || r.cliniko_id} className="rounded-xl border border-slate-100 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">{r.patient_name}</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.status === "overdue" ? "bg-red-100 text-red-700" : r.status === "completed" ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>
                        {r.status === "overdue" ? <AlertCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}{r.status}
                      </span>
                      {!String(r.id || "").startsWith("cliniko-") && (
                        <button onClick={() => openEditRecall(r)} data-testid={`edit-recall-${r.id}`} className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-teal-600" title="Edit recall"><Pencil className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-xs text-slate-500">
                    <span>{r.reason}</span><span className="font-mono">{fmt(r.due_date)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
              <Activity className="h-3.5 w-3.5 text-teal-600" /> Cliniko key server-side · audit logged
            </div>
          </div>
        </div>
      </div>

      <RecallModal open={recallOpen} onOpenChange={setRecallOpen} recall={recallEdit} onSaved={onRecallSaved} />
    </Layout>
  );
}
